<template>
    <div
        ref="header"
        class="w-full heading-xs transition-colors duration-300 z-10 text-neutral-extra-strong"
        :class="headerClass"
    >
        <BalancedHeader
            class="items-center px-1 overflow-hidden min-h-header"
            :enabled="isCentered"
            @update:side-width="menuSidesWidth = $event"
        >
            <template
                v-if="!hideBack"
                #left
            >
                <!-- Teleport items here via <Teleport to="#header-menu-left"> -->
                <div
                    id="header-menu-left"
                    class="mr-1"
                >
                    <slot name="back">
                        <IonBackButton
                            v-show="false"
                            ref="ionicBack"
                        />
                        <button
                            v-if="showBackButton"
                            data-cy="navigate-back"
                            class="appearance-none h-[38px] w-[38px] flex items-center justify-center"
                            :class="{
                                'rounded-full shadow bg-white': headerImage,
                            }"
                            @click="goBack"
                        >
                            <PhIcon
                                v-if="isIos"
                                icon="caret-left"
                                weight="light"
                                :size="24"
                            />
                            <PhIcon
                                v-else
                                icon="arrow-left"
                                weight="light"
                                :size="24"
                            />
                        </button>
                    </slot>
                </div>
            </template>

            <span
                class="line-clamp-1 truncate content-lg-ux-medium"
                :class="{
                    'text-left mr-auto': !isCentered,
                    'text-center mx-auto': isCentered,
                    'ml-3': !isCentered && !showBackButton,
                }"
            >
                <slot name="title" />
            </span>

            <template #right>
                <!-- Teleport items here via <Teleport to="#header-menu"> -->
                <div
                    id="header-menu"
                    class="ml-1"
                >
                    <slot name="menu" />
                </div>
            </template>
        </BalancedHeader>

        <!-- Teleport items here via <Teleport to="#header-contextual"> -->
        <div id="header-contextual">
            <slot name="contextual" />
        </div>
    </div>
</template>

<script setup>
    import BalancedHeader from '@/components/ui/BalancedHeader.vue'

    const props = defineProps({
        headerImage: Boolean,
        hasScrolled: Boolean,
        hideBack: Boolean,
        centered: {
            type: Boolean,
            default: null,
        },
        safeAreaTop: {
            type: Boolean,
            default: true,
        },
        hideBorder: Boolean,
    })

    const header = useTemplateRef('header')
    const ionicBack = useTemplateRef('ionicBack')

    const router = useIonRouter()
    const deviceStore = useDeviceStore()
    const appStore = useAppStore()

    const { width: headerWidth } = useElementSize(header)
    const menuSidesWidth = ref(0)

    const { isIos } = storeToRefs(deviceStore)
    const { initialUrlSearch } = storeToRefs(appStore)

    const useBlur = computed(() => !initialUrlSearch.value.includes('emulate'))

    // Default to centered on iOS unless explicitly overridden
    const isCentered = computed(() => {
        // If we have too many menu items (more than 20% the header width), avoid centering the title as it will look cramped
        if (menuSidesWidth.value > headerWidth.value * 0.2) {
            return false
        }
        return props.centered ?? isIos.value
    })

    const headerClass = computed(() => {
        return [
            props.headerImage ? 'bg-transparent' : 'surface-primary',
            {
                'border-neutral-subtle!': props.hasScrolled && !props.headerImage,
                'use-blur': useBlur.value && props.hasScrolled && !props.headerImage,
                'pt-(--beyond-safe-area-inset-top)': props.safeAreaTop,
                'border-b border-b-transparent': !props.hideBorder,
            },
        ]
    })

    const showBackButton = computed(() => {
        return router.canGoBack() && !props.hideBack
    })

    const goBack = () => {
        // Ionic back buttons work differently to the back method on the ionic router
        // Tabs have their own navigation stack and the ionic back button works by triggering a method on the closest ion-nav element
        // Since this functionality isn't publicly exposed, we achieve this by triggering a click on a hidden back button
        // Without this hack the back button of deep route inside a tab won't return to the previous route belonging to that tab, but the previous route, which could be a different tab
        ionicBack.value.$el.click()
    }
</script>
